import { useEffect, useRef, useState, type ReactNode } from 'react';
import { isBlueHQ, screenInProduct } from '../shared/site.ts';
import { NewTow, TowDetail, TowsPage } from '../ops/Tows.tsx';
import { NewNotice, NoticeDetail, NoticesPage, PioItem, PioPage, PioSettings, RecipientsPage, TipDetail, TipsPage } from '../ops/Pio.tsx';
import { EvaluationDetail, EvaluationsPage, HrEmployee, HrEmployees, InspectionHistory, InspectionsPage, NewInspection, PayPlan, PositionsPage } from '../ops/Hr.tsx';
import { PortalApp } from '../portal/PortalApp.tsx';
import { EwsDashboard, EwsOfficer, EwsSettings, MyRecord, PersonnelDetail, PersonnelList, UofList, UofReport } from '../ops/Personnel.tsx';
import { CaseActivity, IntelDetail, IntelPage, NewIntel } from '../ops/Intel.tsx';
import { MyTraining, TrainingClass, TrainingClasses, TrainingMrn, TrainingOfficer, TrainingOfficers, TrainingReview, TrainingRoster, QualRoster } from '../ops/Training.tsx';
import { DeputyHome } from '../field/Home.tsx';
import { ActivePapersSearch, EvictionsPage, OnCallPage, PhoneNumbersPage } from '../ops/FieldInfo.tsx';
import { NewTicket, TicketDetail, TicketsPage } from '../ops/Tickets.tsx';
import { BlueMark, BlueWordmark } from '../shared/BlueMark.tsx';
import { useTheme } from '../shared/theme.ts';
import { Icon } from '../shared/icons.tsx';
import { SearchPage } from './Search.tsx';
import { QuickJump, type JumpItem } from './QuickJump.tsx';
import { api } from '../shared/api.ts';
import { looksLikePaperNumber, useGlobalScanner } from '../shared/scanner.ts';
import { useAuth } from '../shared/auth.tsx';
import { Link, match, useRouter } from '../shared/router.tsx';
import { Dashboard } from './Dashboard.tsx';
import { PaperList } from './PaperList.tsx';
import { Intake } from './Intake.tsx';
import { PaperDetail } from './PaperDetail.tsx';
import { CustodyStation } from './Custody.tsx';
import { CourtReturns } from './CourtReturns.tsx';
import { ReviewQueue } from './Review.tsx';
import { Admin } from './Admin.tsx';
import { AuditLog } from './Audit.tsx';
import { PrintLabel } from './PrintLabel.tsx';
import { PrintBatch } from './PrintBatch.tsx';
import { Reconcile } from './Reconcile.tsx';
import { Stack } from './Stack.tsx';
import { Schedule } from './Schedule.tsx';
import { BoloPage } from './Bolo.tsx';
import { PeopleSearch, PersonProfile } from './People.tsx';
import { DoorHangers } from './DoorHangers.tsx';
import { MyProfile } from '../shared/MySignature.tsx';
import { WritBook } from './WritBook.tsx';
import { Metrics } from './Metrics.tsx';
import { TransferDetail, TransfersPage } from './Transfers.tsx';
import { ImportPage } from './Import.tsx';
import { EjectmentsPage } from './Ejectments.tsx';
import { ScanAIBatch, ScanAIDraft, ScanAIHome, ScanAILearning } from './ScanAI.tsx';
import { OfficeMap } from './Map.tsx';
import { Dispatch } from './Dispatch.tsx';
import { ReportsPage } from '../rms/ReportsPage.tsx';
import { ReportWizard } from '../rms/ReportWizard.tsx';
import { ReportPrint } from '../rms/ReportPrint.tsx';
import { AlertsSetup, NarcanPage } from '../ops/Alerts.tsx';
import { SchoolsPage, SchoolDetail, DrillPage, DrillPrint } from '../ops/Schools.tsx';
import { FormsPage, NewForm, FormDetail, FormPrint } from '../ops/Forms.tsx';
import { TowPrint, TicketPrint } from '../ops/PaperPrints.tsx';
import { EscortsPage, NewEscort, EscortDetail } from '../ops/Escorts.tsx';
import { BolosPage, NewBolo, BoloDetail } from '../ops/Bolos.tsx';
import { StatsPage } from '../field/DeputyAdmin.tsx';
import { ApplicantsPage, NewApplicant, ApplicantDetail } from '../ops/Applicants.tsx';
import { FoiaPage, NewFoia, FoiaSettings, FoiaDetail } from '../ops/Foia.tsx';
import { BwcPage, NewBwc, BwcDetail } from '../ops/Bwc.tsx';
import { EventsPage, NewEvent, EventDetail, SchoolDetailsPage, NewSchoolDetail } from '../ops/Events.tsx';
import { DeskReportsPage, NewDeskReport, DeskReportDetail, SafetyPage, NewSafety, SafetyDetail } from '../ops/SroPages.tsx';
import { ShutoffsPage, OutsideContactsCard } from '../ops/Shutoffs.tsx';
import { HouseCheckDetail, HouseChecksPage } from '../ops/HouseChecks.tsx';
import { DailyLogsPage, DailyLogView, MyDailyLogPage } from '../ops/DailyLog.tsx';
import { IaCaseDetail, IaListPage, NewIaCase } from '../ops/IA.tsx';
import { SimilarCasesPage } from '../rms/SimilarCases.tsx';
import { FieldMapPage } from '../ops/FieldMap.tsx';
import { ResourcesPage } from '../ops/Resources.tsx';
import { CommandBoardPage, CommandBoardsPage, NewCommandBoard } from '../ops/Command.tsx';
import { ArrestFeedPage } from '../ops/ArrestFeed.tsx';
import { PoliciesPage, NewPolicy, PolicyDetail, PolicyImport } from '../ops/Policies.tsx';
import { MileagePage } from '../ops/Mileage.tsx';
import { CallNumbersPage } from '../ops/CallNumbers.tsx';
import { AssetReportPage } from '../ops/AssetReport.tsx';
import { DuesPage, MyDuesPage } from '../ops/Dues.tsx';

import { CrimeAnalytics } from '../rms/Analytics.tsx';
import { SupplementEditor } from '../rms/SupplementEditor.tsx';
import { EvidenceItem, EvidenceLabels, EvidencePage } from '../rms/Evidence.tsx';
import { Compose, MailPage, MailThread } from '../ops/Mail.tsx';
import { DirectoryPage, OrgChartPage } from '../ops/Directory.tsx';
import { CourtEvent, CourtPage } from '../ops/Court.tsx';
import { CaseFileDetail, CaseFilesPage, CaseTrack, InvestigationsPage, MyCasesPage } from '../ops/Cases.tsx';
import { CertsPage, EquipmentDetail, EquipmentPage, FleetPage, MyGearPage, SparesPage, VehicleDetail } from '../ops/Fleet.tsx';
import { NewPermit, PermitDetail, PermitsPage } from '../ops/Permits.tsx';
import { PhotoFolders, PhotosPage } from '../ops/Photos.tsx';
import { MyWarrants, WarrantDetail, WarrantEntry, WarrantQueue, WarrantRequestDetail, WarrantRequestForm, WarrantsActive } from '../ops/Warrants.tsx';
import { useMailCount } from '../ops/common.tsx';

type NavItem = { to: string; icon: string; label: string; perm: string | string[] };
type Module = { key: string; label: string; groups: { title: string; items: NavItem[] }[] };
const RMS = ['reports.read', 'reports.approve'];
const SWORN = ['reports.write', 'attempts.record'];
const FLEET = ['fleet.manage', 'schedule.manage'];
/** The app's modules (tabs across the top) and each module's side menu. */
export const MODULES: Module[] = [
  { key: 'home', label: 'Home', groups: [{ title: 'Home', items: [
    { to: '/home', icon: 'attention', label: 'Home', perm: [] as string[] },
    { to: '/messages', icon: 'mail', label: 'Messages', perm: [] as string[] },
    { to: '/on-call', icon: 'phone', label: 'On-call magistrate', perm: [] as string[] },
    { to: '/phones', icon: 'phone', label: 'Important phone numbers', perm: [] as string[] },
  ] }] },
  {
    key: 'civil',
    label: 'Civil Process',
    groups: [
      { title: 'Today', items: [
        { to: '/', icon: 'attention', label: 'Needs attention', perm: 'papers.read' },
        { to: '/dispatch', icon: 'dispatch', label: 'Dispatch', perm: 'dispatch.view' },
        { to: '/map', icon: 'map', label: 'Map', perm: 'papers.read' },
        { to: '/schedule', icon: 'calendar', label: 'Deputy schedule', perm: 'custody.court_return' },
      ] },
      { title: 'Papers', items: [
        { to: '/papers', icon: 'papers', label: 'Papers', perm: 'papers.read' },
        { to: '/papers/new', icon: 'intake', label: 'New intake', perm: 'papers.intake' },
        { to: '/scanai', icon: 'scan', label: 'ScanAI intake', perm: 'papers.intake' },
        { to: '/import', icon: 'upload', label: 'Import papers', perm: 'papers.intake' },
        { to: '/ejectments', icon: 'door', label: 'Ejectments', perm: 'papers.read' },
        { to: '/writ-book', icon: 'book', label: 'Writ Book', perm: 'papers.read' },
        { to: '/bolo', icon: 'bolo', label: 'BOLO', perm: 'papers.read' },
        { to: '/door-hangers', icon: 'tag', label: 'Door hangers', perm: 'papers.read' },
      ] },
      { title: 'Custody & returns', items: [
        { to: '/custody', icon: 'barcode', label: 'Custody scan', perm: 'custody.scan' },
        { to: '/reconcile', icon: 'check', label: 'Reconciliation', perm: 'custody.court_return' },
        { to: '/stack', icon: 'layers', label: 'Build a stack', perm: 'custody.court_return' },
        { to: '/court-returns', icon: 'gavel', label: 'Court returns', perm: 'custody.court_return' },
        { to: '/transfers', icon: 'transfer', label: 'Agency transfers', perm: 'papers.read' },
        { to: '/review', icon: 'conflict', label: 'Offline conflicts', perm: 'sync.review_conflicts' },
      ] },
    ],
  },
  {
    key: 'records',
    label: 'Records',
    groups: [
      { title: 'Reports', items: [
        { to: '/reports', icon: 'report', label: 'Incident reports', perm: RMS },
        { to: '/uof', icon: 'attention', label: 'Use of force', perm: [...SWORN, 'reports.approve', 'ews.review'] },
        { to: '/photos', icon: 'upload', label: 'Upload photos', perm: ['reports.write', 'reports.approve', 'evidence.manage'] },
        { to: '/tickets', icon: 'pen', label: 'E-tickets', perm: RMS },
        { to: '/tows', icon: 'tag', label: 'Tow reports', perm: RMS },
        { to: '/forms', icon: 'clipboard', label: 'Forms (rights, custodial, NCIC)', perm: [...RMS, 'dispatch.view', 'warrants.enter'] },
      ] },
      { title: 'Arrests', items: [
        { to: '/arrests', icon: 'arrest', label: 'Recent arrests', perm: [...RMS, 'reports.write', 'dispatch.view', 'warrants.view'] },
        { to: '/daily-log', icon: 'clipboard', label: 'My activity log', perm: ['reports.write'] },
        { to: '/daily-logs', icon: 'clipboard', label: 'Activity logs (review)', perm: ['reports.approve'] },
      ] },
      { title: 'Patrol', items: [
        { to: '/patrol-map', icon: 'map', label: 'Field map', perm: [...RMS, 'reports.write', 'dispatch.view', 'command.start'] },
        { to: '/command', icon: 'attention', label: 'Command boards', perm: [...RMS, 'reports.write', 'dispatch.view', 'command.start'] },
        { to: '/resources', icon: 'people', label: 'Resources on duty', perm: [...RMS, 'reports.write', 'resources.view', 'resources.manage'] },
      ] },
      { title: 'Schools & details', items: [
        { to: '/schools', icon: 'book', label: 'Schools & lockdown drills', perm: [...RMS, 'schools.manage'] },
        { to: '/schools/details', icon: 'calendar', label: 'After-school details', perm: [...RMS, 'reports.write', 'schools.manage'] },
        { to: '/schools/desk', icon: 'clipboard', label: 'SRO desk reports', perm: ['schools.manage', 'reports.approve'] },
        { to: '/schools/safety', icon: 'attention', label: 'School safety concerns', perm: ['schools.manage', 'reports.approve'] },
        { to: '/events', icon: 'calendar', label: 'Events (sign-up)', perm: [...RMS, 'reports.write', 'dispatch.view', 'schedule.manage'] },
        { to: '/bolos', icon: 'bolo', label: 'BOLOs', perm: [...RMS, 'reports.write', 'dispatch.view'] },
        { to: '/escorts', icon: 'calendar', label: 'Funeral escorts', perm: [...RMS, 'reports.write', 'dispatch.view', 'admin.config'] },
        { to: '/house-checks', icon: 'door', label: 'House checks', perm: [...RMS, 'reports.write', 'dispatch.view', 'admin.config'] },
      ] },
      { title: 'Cases', items: [
        { to: '/cases', icon: 'route', label: 'My cases', perm: ['reports.write', 'cases.investigate'] },
        { to: '/narcan', icon: 'attention', label: 'Narcan', perm: ['reports.write', 'reports.approve'] },
        { to: '/investigations', icon: 'search', label: 'Investigations', perm: 'cases.investigate' },
        { to: '/cases/activity', icon: 'chart', label: 'Case activity', perm: ['reports.approve', 'cases.investigate', 'admin.config'] },
        { to: '/intel', icon: 'search', label: 'Field intel', perm: [...SWORN, 'reports.read', 'reports.approve', 'cases.investigate'] },
      ] },
      { title: 'Master indexes', items: [{ to: '/people', icon: 'people', label: 'People & vehicles', perm: ['papers.read', ...RMS] }] },
      { title: 'Permits', items: [{ to: '/permits', icon: 'recycle', label: 'Scrap metal permits', perm: ['permits.manage', 'reports.write'] }] },
    ],
  },
  {
    key: 'warrants',
    label: 'Warrants',
    groups: [
      { title: 'Warrants', items: [
        { to: '/warrants', icon: 'search', label: 'Active warrants', perm: ['warrants.view', 'warrants.enter'] },
        { to: '/warrants/request', icon: 'plus', label: 'Request a warrant', perm: 'warrants.request' },
        { to: '/warrants/mine', icon: 'route', label: 'My warrants', perm: ['warrants.request'] },
        { to: '/warrants/queue', icon: 'clipboard', label: 'Requests', perm: ['warrants.enter', 'warrants.sign'] },
        { to: '/warrants/entry', icon: 'intake', label: 'Warrant entry', perm: 'warrants.enter' },
      ] },
    ],
  },
  {
    key: 'court',
    label: 'Court',
    groups: [
      { title: 'Court', items: [
        { to: '/court', icon: 'gavel', label: 'Court calendar', perm: 'court.view' },
        { to: '/civil-papers', icon: 'papers', label: 'Civil papers from my court', perm: 'portal.use' },
        { to: '/on-call', icon: 'phone', label: 'On-call magistrate', perm: [] as string[] },
        { to: '/case-files', icon: 'folderShare', label: 'Case files', perm: ['casefiles.view', 'casefiles.share'] },
        { to: '/directory', icon: 'directory', label: 'Directory', perm: 'directory.view' },
      ] },
    ],
  },
  {
    key: 'pio',
    label: 'PIO',
    groups: [
      { title: 'Critical notices', items: [
        { to: '/pio/notices/new', icon: 'attention', label: 'New critical notice', perm: [...SWORN, 'dispatch.view', 'reports.approve', 'admin.config'] },
        { to: '/pio/notices', icon: 'clipboard', label: 'Critical notices', perm: [...SWORN, 'dispatch.view', 'reports.approve', 'admin.config', 'pio.write', 'pio.approve'] },
        { to: '/pio/recipients', icon: 'phone', label: 'Notice recipients', perm: [...SWORN, 'dispatch.view', 'reports.approve', 'admin.config'] },
      ] },
      { title: 'Public information', items: [
        { to: '/pio', icon: 'pen', label: 'Releases & statements', perm: ['pio.write', 'pio.approve', 'admin.config'] },
        { to: '/pio/foia', icon: 'folder', label: 'FOIA requests', perm: ['pio.write', 'pio.approve', 'admin.config'] },
      ] },
      { title: 'Tips', items: [
        { to: '/tips', icon: 'search', label: 'Tips', perm: 'tips.manage' },
        { to: '/pio/settings', icon: 'settings', label: 'Tip page, Jotform & CAD', perm: ['admin.config', 'tips.manage'] },
      ] },
    ],
  },
  { key: 'evidence', label: 'Evidence', groups: [{ title: 'Property & evidence', items: [
    { to: '/evidence', icon: 'evidence', label: 'Evidence room', perm: RMS },
    { to: '/evidence?tab=court', icon: 'gavel', label: 'Court orders', perm: 'evidence.manage' },
  ] }] },
  {
    key: 'agency',
    label: 'Agency',
    groups: [
      { title: 'People', items: [
        { to: '/messages', icon: 'mail', label: 'Messages', perm: [] as string[] },
        { to: '/directory', icon: 'directory', label: 'Directory', perm: 'directory.view' },
        { to: '/phones', icon: 'phone', label: 'Important phone numbers', perm: [] as string[] },
        { to: '/dues', icon: 'chart', label: 'No-Shave dues', perm: 'dues.manage' },
        { to: '/call-numbers', icon: 'radio', label: 'Radio call numbers', perm: ['directory.view', 'admin.org'] },
        { to: '/org', icon: 'org', label: 'Org chart & shifts', perm: ['admin.org', 'schedule.manage'] },
        { to: '/alerts', icon: 'bell', label: 'Alerts', perm: ['admin.config', 'admin.users', 'pio.approve'] },
      ] },
      { title: 'Resources', items: [
        { to: '/policies', icon: 'book', label: 'Policies', perm: [] as string[] },
      ] },
      { title: 'Personnel', items: [
        { to: '/personnel', icon: 'clipboard', label: 'Write-ups & commendations', perm: ['reports.approve', 'personnel.manage', 'ews.review'] },
        { to: '/ews', icon: 'attention', label: 'Early warning', perm: 'ews.review' },
        { to: '/ia', icon: 'lock', label: 'Internal affairs', perm: 'ia.manage' },
        { to: '/personnel/me', icon: 'user', label: 'My record', perm: [...SWORN, 'reports.approve', 'dispatch.view', 'papers.intake'] },
        { to: '/hr/me', icon: 'chart', label: 'My pay', perm: [...SWORN, 'reports.approve', 'dispatch.view', 'papers.intake'] },
        { to: '/evaluations', icon: 'clipboard', label: 'Evaluations', perm: ['reports.approve', 'hr.manage'] },
        { to: '/hr', icon: 'people', label: 'Employees & pay', perm: 'hr.manage' },
        { to: '/hr/applicants', icon: 'user', label: 'Applicants', perm: ['hr.manage', 'personnel.manage', 'dispatch.view'] },
        { to: '/bwc', icon: 'monitor', label: 'Body cam reviews', perm: ['reports.approve', 'ews.review', 'personnel.manage'] },
        { to: '/shutoffs', icon: 'lock', label: 'Shut-offs (cards, RFID, fuel)', perm: ['admin.config', 'hr.manage', 'fleet.manage'] },
        { to: '/hr/positions', icon: 'org', label: 'Positions', perm: ['hr.manage', 'admin.org', 'directory.view'] },
      ] },
      { title: 'Training', items: [
        { to: '/training', icon: 'book', label: 'My training', perm: [...SWORN, 'training.view', 'training.manage', 'dispatch.view', 'papers.intake'] },
        { to: '/training/officers', icon: 'people', label: 'Training officer', perm: ['training.manage', 'training.view'] },
        { to: '/training/review', icon: 'clipboard', label: 'Certificates to file', perm: 'training.manage' },
        { to: '/training/classes', icon: 'calendar', label: 'Training classes', perm: 'training.manage' },
      ] },
      { title: 'Fleet & equipment', items: [
        { to: '/fleet', icon: 'car', label: 'Fleet', perm: FLEET },
        { to: '/equipment', icon: 'radio', label: 'Equipment', perm: FLEET },
        { to: '/equipment/report', icon: 'clipboard', label: 'Equipment report', perm: [...FLEET, 'reports.approve'] },
        { to: '/spares', icon: 'car', label: 'Spare cars', perm: [...SWORN, ...FLEET] },
        { to: '/inventory', icon: 'clipboard', label: 'Inventory checks', perm: FLEET },
        { to: '/my-gear', icon: 'clipboard', label: 'My gear', perm: SWORN },
        { to: '/mileage', icon: 'car', label: 'My mileage', perm: SWORN },
        { to: '/inspections', icon: 'car', label: 'Vehicle inspections', perm: [...SWORN, ...FLEET, 'reports.approve'] },
      ] },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    groups: [
      { title: 'Analytics', items: [
        { to: '/reports/analytics', icon: 'chart', label: 'Crime analytics', perm: RMS },
        { to: '/reports/similar', icon: 'search', label: 'Similar cases', perm: [...RMS, 'cases.investigate'] },
        { to: '/metrics', icon: 'pulse', label: 'Civil process metrics', perm: 'schedule.manage' },
      ] },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    groups: [
      { title: 'Administration', items: [
        { to: '/admin', icon: 'settings', label: 'Settings & users', perm: ['admin.config', 'admin.rules'] },
        { to: '/audit', icon: 'audit', label: 'Audit log', perm: 'audit.read' },
      ] },
      { title: 'My account', items: [{ to: '/me', icon: 'user', label: 'My profile', perm: [] as string[] }, { to: '/stats', icon: 'chart', label: 'My activity', perm: [] as string[] }] },
    ],
  },
];

/** Which module a page belongs to. */
export function moduleFor(p: string): string {
  if (p === '/home') return 'home';
  if (p.startsWith('/reports/analytics') || p.startsWith('/metrics')) return 'analytics';
  if (/^\/(court|case-files|civil-papers)(\/|$)/.test(p)) return 'court';
  if (/^\/(pio|tips)(\/|$)/.test(p)) return 'pio';
  if (/^\/warrants(\/|$)/.test(p)) return 'warrants';
  if (/^\/(messages|directory|org|fleet|equipment|spares|inventory|my-gear|training|personnel|ews|hr|evaluations|inspections|alerts|bwc|shutoffs|policies|mileage|call-numbers|dues|ia)(\/|$)/.test(p)) return 'agency';
  if (/^\/(cases|investigations|permits|photos|intel|tows|tickets|forms|schools|narcan|escorts|bolos|events|arrests|house-checks|daily-logs?|patrol-map|command|resources)(\/|$)/.test(p)) return 'records';
  if (p.startsWith('/evidence')) return 'evidence';
  if (p.startsWith('/reports') || p.startsWith('/people') || p.startsWith('/search') || p.startsWith('/uof')) return 'records';
  if (p.startsWith('/admin') || p.startsWith('/audit') || p === '/me' || p === '/stats') return 'admin';
  return 'civil';
}

const initials = (n: string) =>
  n
    .replace(/^(Sgt|Lt|Cpl|Dep|Deputy|Capt|Chief|Sheriff|Ofc|Officer)\.?\s+/i, '')
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');

function UserMenu() {
  const { me, logout, can } = useAuth();
  const [open, setOpen] = useState(false);
  const [theme, setTheme] = useTheme();
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => ref.current && !ref.current.contains(e.target as Node) && setOpen(false);
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);
  const name = me!.user.displayName;
  return (
    <div className="umenu" ref={ref}>
      <button type="button" aria-haspopup="menu" aria-expanded={open} aria-label="Account menu" onClick={() => setOpen((o) => !o)}>
        <span className="avatar" aria-hidden>
          {initials(name)}
        </span>
        <span className="who-name">{name}</span> <Icon name="chevronDown" size={14} />
      </button>
      {open && (
        <div className="umenu-pop" role="menu" onClick={(e) => (e.target as HTMLElement).closest('a,button.um-i') && setOpen(false)}>
          <div className="um-h">
            <strong>{name}</strong>
            <div className="muted small">
              {me!.user.role.replace('_', ' ')} · {me!.agency.name}
            </div>
          </div>
          <Link to="/me">
            <Icon name="user" /> My profile, signature &amp; sign-in
          </Link>
          {(can('attempts.record') || can('papers.read')) && (
            <Link to="/field">
              <Icon name="phone" /> Open the deputy app
            </Link>
          )}
          <div className="um-k">Theme</div>
          <div className="theme-seg" role="radiogroup" aria-label="Theme">
            {(
              [
                ['light', 'Light', 'sun'],
                ['dark', 'Dark', 'moon'],
                ['auto', 'Device', 'monitor'],
              ] as const
            ).map(([k, l, ic]) => (
              <button key={k} type="button" role="radio" aria-checked={theme === k} className={theme === k ? 'on' : ''} onClick={() => setTheme(k)}>
                <Icon name={ic} size={15} /> {l}
              </button>
            ))}
          </div>
          <button type="button" className="um-i" onClick={logout}>
            <Icon name="logout" /> Sign out
          </button>
        </div>
      )}
    </div>
  );
}

/** Messages, with the unread count. */
function MailButton() {
  const c = useMailCount();
  const n = c?.unread ?? 0;
  return (
    <Link to="/messages" className={`mail-btn ${c?.overdue ? 'overdue' : ''}`} aria-label={`Messages${n ? `, ${n} unread` : ''}`} title={c?.overdue ? `${c.overdue} reply overdue` : 'Messages'}>
      <Icon name="mail" size={18} />
      {n > 0 && <span className="mail-n">{n > 99 ? '99+' : n}</span>}
    </Link>
  );
}

/** One box for everything: case #, paper #, name, address, tag, VIN, serial. */
function GlobalSearch() {
  const { navigate } = useRouter();
  const [q, setQ] = useState('');
  const box = useRef<HTMLInputElement>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (e.key === '/' && !e.ctrlKey && !e.metaKey && !/^(INPUT|TEXTAREA|SELECT)$/.test(t.tagName) && !t.isContentEditable) {
        e.preventDefault();
        box.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return (
    <form
      className="gsearch"
      role="search"
      onSubmit={(e) => {
        e.preventDefault();
        if (q.trim().length >= 2) navigate(`/search?q=${encodeURIComponent(q.trim())}`);
      }}
    >
      <Icon name="search" size={15} className="gs-ico" />
      <input ref={box} aria-label="Search everything" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search case #, name, address, tag, VIN…" />
      <kbd>/</kbd>
    </form>
  );
}

export function OfficeApp() {
  const { me, can } = useAuth();
  const { path, navigate } = useRouter();
  const p = path.split('?')[0]!;
  const [scanMsg, setScanMsg] = useState<string | null>(null);
  // Scan a paper barcode from any screen → that paper's page. Pages with their own scan box handle scans themselves.
  const ownScanBox = ['/reconcile', '/custody', '/court-returns', '/papers/print', '/evidence'].includes(p) || p.startsWith('/scanai');
  useGlobalScanner(async (code) => {
    const n = looksLikePaperNumber(code);
    if (!n) return setScanMsg(`Scanned “${code}” — not a paper barcode.`);
    try {
      const d = await api.get<any>(`/api/papers/by-number/${encodeURIComponent(n)}`);
      setScanMsg(null);
      navigate(`/papers/${d.paper.id}`);
    } catch {
      setScanMsg(`No paper found for ${n}.`);
    }
  }, can('papers.read') && !ownScanBox);

  if (p === '/papers/print') return <PrintBatch />;
  if (p === '/evidence/labels') return <EvidenceLabels />;
  if (match('/reports/:id/print', p)) return <ReportPrint id={match('/reports/:id/print', p)!.id!} />;
  if (match('/schools/drills/:id/print', p)) return <DrillPrint id={match('/schools/drills/:id/print', p)!.id!} />;
  if (match('/forms/:id/print', p)) return <FormPrint id={match('/forms/:id/print', p)!.id!} />;
  if (match('/tows/:id/print', p)) return <TowPrint id={match('/tows/:id/print', p)!.id!} />;
  if (match('/tickets/:id/print', p)) return <TicketPrint id={match('/tickets/:id/print', p)!.id!} />;
  if (match('/papers/:id/label', p)) return <PrintLabel id={match('/papers/:id/label', p)!.id!} />;

  let page: ReactNode;
  let m: Record<string, string> | null;
  // BlueHQ: everyone's home page (announcements, attention, upcoming, messages).
  if (isBlueHQ() && (p === '/home' || ((p === '/' || p === '/dashboard') && !can('papers.read')))) page = <DeputyHome />;
  else if ((p === '/' || p === '/dashboard') && !can('papers.read') && can('dispatch.view')) page = <Dispatch />;
  // Magistrate / solicitor: the court calendar is home.
  else if ((p === '/' || p === '/dashboard') && !can('papers.read') && can('court.view')) page = <CourtPage />;
  else if (p === '/warrants') page = <WarrantsActive />;
  else if (p === '/warrants/request') page = <WarrantRequestForm />;
  else if (p === '/warrants/mine') page = <MyWarrants />;
  else if (p === '/warrants/queue') page = <WarrantQueue />;
  else if (p === '/warrants/entry') page = <WarrantEntry key={path} />;
  else if ((m = match('/warrants/requests/:id', p))) page = <WarrantRequestDetail key={m.id} id={m.id!} />;
  else if ((m = match('/warrants/:id', p))) page = <WarrantDetail key={m.id} id={m.id!} />;
  else if (p === '/messages') page = <MailPage />;
  else if (p === '/photos') page = <PhotosPage key={path} />;
  else if ((m = match('/photos/case/:id', p))) page = <PhotoFolders key={m.id} incidentId={m.id!} />;
  else if (p === '/messages/new') page = <Compose />;
  else if ((m = match('/messages/:id', p))) page = <MailThread key={m.id} id={m.id!} />;
  else if (p === '/directory') page = <DirectoryPage />;
  else if (p === '/org') page = <OrgChartPage />;
  else if (p === '/court') page = <CourtPage />;
  else if ((m = match('/court/:id', p))) page = <CourtEvent key={m.id} id={m.id!} />;
  else if (p === '/case-files') page = <CaseFilesPage />;
  else if ((m = match('/case-files/:id', p))) page = <CaseFileDetail key={m.id} id={m.id!} />;
  else if (p === '/cases') page = <MyCasesPage />;
  else if (p === '/cases/activity') page = <CaseActivity />;
  else if (p === '/civil-papers') page = <PortalApp embedded />;
  else if (p === '/hr') page = <HrEmployees />;
  else if (p === '/hr/me') page = <HrEmployee />;
  else if (p === '/hr/pay-plan') page = <PayPlan />;
  else if (p === '/hr/positions') page = <PositionsPage />;
  else if ((m = match('/hr/employees/:id', p))) page = <HrEmployee key={m.id} id={m.id!} />;
  else if (p === '/evaluations') page = <EvaluationsPage />;
  else if ((m = match('/evaluations/:id', p))) page = <EvaluationDetail key={m.id} id={m.id!} />;
  else if (p === '/inspections') page = <InspectionsPage />;
  else if ((m = match('/inspections/:id/new', p))) page = <NewInspection key={m.id} vehicleId={m.id!} />;
  else if ((m = match('/inspections/:id', p))) page = <InspectionHistory key={m.id} vehicleId={m.id!} />;
  else if (p === '/uof') page = <UofList />;
  else if ((m = match('/uof/:id', p))) page = <UofReport key={m.id} id={m.id!} />;
  else if (p === '/personnel') page = <PersonnelList />;
  else if (p === '/personnel/me') page = <MyRecord />;
  else if ((m = match('/personnel/:id', p))) page = <PersonnelDetail key={m.id} id={m.id!} />;
  else if (p === '/ews') page = <EwsDashboard />;
  else if (p === '/ia') page = <IaListPage />;
  else if (p === '/ia/new') page = <NewIaCase />;
  else if ((m = match('/ia/:id', p))) page = <IaCaseDetail key={m.id} id={m.id!} />;
  else if (p === '/ews/settings') page = <EwsSettings />;
  else if ((m = match('/ews/officers/:id', p))) page = <EwsOfficer key={m.id} id={m.id!} />;
  else if (p === '/intel') page = <IntelPage />;
  else if (p === '/intel/new') page = <NewIntel />;
  else if ((m = match('/intel/:id', p))) page = <IntelDetail key={m.id} id={m.id!} />;
  else if ((m = match('/cases/:id', p))) page = <CaseTrack key={m.id} id={m.id!} />;
  else if (p === '/investigations') page = <InvestigationsPage />;
  else if (p === '/fleet') page = <FleetPage />;
  else if ((m = match('/fleet/:id', p))) page = <VehicleDetail key={m.id} id={m.id!} />;
  else if (p === '/equipment/report') page = <AssetReportPage />;
  else if (p === '/equipment') page = <EquipmentPage />;
  else if ((m = match('/equipment/:id', p))) page = <EquipmentDetail key={m.id} id={m.id!} />;
  else if (p === '/spares') page = <SparesPage />;
  else if (p === '/inventory') page = <CertsPage />;
  else if (p === '/my-gear') page = <MyGearPage />;
  else if (p === '/permits') page = <PermitsPage />;
  else if (p === '/permits/new') page = <NewPermit />;
  else if ((m = match('/permits/:id', p))) page = <PermitDetail key={m.id} id={m.id!} />;
  else if (p === '/' || p === '/dashboard') page = <Dashboard />;
  else if (p === '/dispatch') page = <Dispatch />;
  else if (p === '/reports') page = <ReportsPage />;
  else if (p === '/reports/analytics') page = <CrimeAnalytics />;
  else if (p === '/reports/similar') page = <SimilarCasesPage />;
  else if (p === '/reports/arrests') page = <ArrestFeedPage />;
  else if (p === '/evidence') page = <EvidencePage key={path} />;
  else if ((m = match('/evidence/:id', p))) page = <EvidenceItem key={m.id} id={m.id!} />;
  else if ((m = match('/reports/supplements/:id', p))) page = <SupplementEditor key={m.id} id={m.id!} />;
  else if ((m = match('/reports/:id', p))) page = <ReportWizard key={m.id} id={m.id!} />;
  else if (p === '/papers') page = <PaperList />;
  else if (p === '/papers/new') page = <Intake />;
  else if (p === '/map') page = <OfficeMap />;
  else if ((m = match('/papers/:id', p))) page = <PaperDetail id={m.id!} />;
  else if (p === '/scanai') page = <ScanAIHome />;
  else if (p === '/scanai/learning') page = <ScanAILearning />;
  else if ((m = match('/scanai/batches/:id', p))) page = <ScanAIBatch id={m.id!} />;
  else if ((m = match('/scanai/drafts/:id', p))) page = <ScanAIDraft id={m.id!} />;
  else if (p === '/custody') page = <CustodyStation />;
  else if (p === '/reconcile') page = <Reconcile />;
  else if (p === '/stack') page = <Stack />;
  else if (p === '/transfers') page = <TransfersPage />;
  else if (p === '/import') page = <ImportPage />;
  else if (p === '/ejectments') page = <EjectmentsPage />;
  else if ((m = match('/transfers/:id', p))) page = <TransferDetail id={m.id!} />;
  else if (p === '/schedule') page = <Schedule />;
  else if (p === '/bolo') page = <BoloPage />;
  else if (p === '/people') page = <PeopleSearch />;
  else if (p === '/search') page = <SearchPage />;
  else if (p === '/door-hangers') page = <DoorHangers />;
  else if (p === '/me') page = <MyProfile />;
  else if (p === '/pio') page = <PioPage />;
  else if (p === '/pio/notices') page = <NoticesPage />;
  else if (p === '/pio/notices/new') page = <NewNotice />;
  else if ((m = match('/pio/notices/:id', p))) page = <NoticeDetail key={m.id} id={m.id!} />;
  else if (p === '/pio/recipients') page = <RecipientsPage />;
  else if (p === '/hr/applicants') page = <ApplicantsPage />;
  else if (p === '/hr/applicants/new') page = <NewApplicant />;
  else if ((m = match('/hr/applicants/:id', p))) page = <ApplicantDetail key={m.id} id={m.id!} />;
  else if (p === '/pio/foia') page = <FoiaPage />;
  else if (p === '/pio/foia/new') page = <NewFoia />;
  else if (p === '/pio/foia/settings') page = <FoiaSettings />;
  else if ((m = match('/pio/foia/:id', p))) page = <FoiaDetail key={m.id} id={m.id!} />;
  else if (p === '/bwc') page = <BwcPage />;
  else if (p === '/bwc/new') page = <NewBwc />;
  else if ((m = match('/bwc/:id', p))) page = <BwcDetail key={m.id} id={m.id!} />;
  else if (p === '/events') page = <EventsPage />;
  else if (p === '/events/new') page = <NewEvent />;
  else if ((m = match('/events/:id', p))) page = <EventDetail key={m.id} id={m.id!} />;
  else if (p === '/schools/details') page = <SchoolDetailsPage />;
  else if (p === '/schools/details/new') page = <NewSchoolDetail />;
  else if (p === '/schools/desk') page = <DeskReportsPage />;
  else if (p === '/schools/desk/new') page = <NewDeskReport />;
  else if ((m = match('/schools/desk/:id', p))) page = <DeskReportDetail key={m.id} id={m.id!} />;
  else if (p === '/schools/safety') page = <SafetyPage />;
  else if (p === '/schools/safety/new') page = <NewSafety />;
  else if ((m = match('/schools/safety/:id', p))) page = <SafetyDetail key={m.id} id={m.id!} />;
  else if (p === '/dues') page = <DuesPage />;
  else if (p === '/dues/me') page = <MyDuesPage />;
  else if (p === '/call-numbers') page = <CallNumbersPage />;
  else if (p === '/mileage') page = <MileagePage />;
  else if (p === '/policies') page = <PoliciesPage />;
  else if (p === '/policies/new') page = <NewPolicy />;
  else if (p === '/policies/import') page = <PolicyImport />;
  else if ((m = match('/policies/:id', p))) page = <PolicyDetail key={m.id} id={m.id!} />;
  else if (p === '/shutoffs') page = <ShutoffsPage />;
  else if (p === '/arrests') page = <ArrestFeedPage />;
  else if (p === '/stats') page = <StatsPage online />;
  else if (p === '/pio/settings') page = <PioSettings />;
  else if ((m = match('/pio/:id', p))) page = <PioItem key={m.id} id={m.id!} />;
  else if (p === '/tips') page = <TipsPage />;
  else if ((m = match('/tips/:id', p))) page = <TipDetail key={m.id} id={m.id!} />;
  else if (p === '/training') page = <MyTraining />;
  else if (p === '/training/officers') page = <TrainingOfficers />;
  else if (p === '/training/roster') page = <TrainingRoster />;
  else if (p === '/alerts') page = <><AlertsSetup /><div className="ops stack"><OutsideContactsCard /></div></>;
  else if (p === '/narcan') page = <NarcanPage />;
  else if (p === '/training/quals') page = <QualRoster />;
  else if ((m = match('/training/officers/:id/mrn', p))) page = <TrainingMrn key={m.id} id={m.id!} />;
  else if ((m = match('/training/officers/:id', p))) page = <TrainingOfficer key={m.id} id={m.id!} />;
  else if (p === '/training/review') page = <TrainingReview />;
  else if (p === '/training/classes') page = <TrainingClasses />;
  else if ((m = match('/training/classes/:id', p))) page = <TrainingClass key={m.id} id={m.id!} />;
  else if (p === '/tows') page = <TowsPage />;
  else if (p === '/on-call') page = <OnCallPage />;
  else if (p === '/phones') page = <PhoneNumbersPage />;
  else if (p === '/evictions') page = <EvictionsPage />;
  else if (p === '/civil-search') page = <ActivePapersSearch />;
  else if (p === '/tows/new') page = <NewTow />;
  else if ((m = match('/tows/:id', p))) page = <TowDetail key={m.id} id={m.id!} />;
  else if (p === '/bolos') page = <BolosPage />;
  else if (p === '/bolos/new') page = <NewBolo />;
  else if ((m = match('/bolos/:id', p))) page = <BoloDetail key={m.id} id={m.id!} />;
  else if (p === '/patrol-map') page = <FieldMapPage />;
  else if (p === '/resources') page = <ResourcesPage />;
  else if (p === '/command') page = <CommandBoardsPage />;
  else if (p === '/command/new') page = <NewCommandBoard />;
  else if ((m = match('/command/:id', p))) page = <CommandBoardPage key={m.id} id={m.id!} />;
  else if (p === '/daily-log') page = <MyDailyLogPage />;
  else if (p === '/daily-logs') page = <DailyLogsPage />;
  else if ((m = match('/daily-log/:id', p))) page = <DailyLogView key={m.id} id={m.id!} />;
  else if (p === '/house-checks') page = <HouseChecksPage />;
  else if ((m = match('/house-checks/:id', p))) page = <HouseCheckDetail key={m.id} id={m.id!} />;
  else if (p === '/escorts') page = <EscortsPage />;
  else if (p === '/escorts/new') page = <NewEscort />;
  else if ((m = match('/escorts/:id', p))) page = <EscortDetail key={m.id} id={m.id!} />;
  else if (p === '/schools') page = <SchoolsPage />;
  else if ((m = match('/schools/drills/:id', p))) page = <DrillPage key={m.id} id={m.id!} />;
  else if ((m = match('/schools/:id', p))) page = <SchoolDetail key={m.id} id={m.id!} />;
  else if (p === '/forms') page = <FormsPage />;
  else if (p === '/forms/new') page = <NewForm />;
  else if ((m = match('/forms/:id', p))) page = <FormDetail key={m.id} id={m.id!} />;
  else if (p === '/tickets') page = <TicketsPage />;
  else if (p === '/tickets/new') page = <NewTicket />;
  else if ((m = match('/tickets/:id', p))) page = <TicketDetail key={m.id} id={m.id!} />;
  else if (p === '/writ-book') page = <WritBook />;
  else if (p === '/metrics') page = <Metrics />;
  else if (p === '/people/profile') page = <PersonProfile />;
  else if (p === '/court-returns') page = <CourtReturns />;
  else if (p === '/review') page = <ReviewQueue />;
  else if (p === '/audit') page = <AuditLog />;
  else if (p.startsWith('/admin')) page = <Admin />;
  else page = <p>Page not found.</p>;
  if (!screenInProduct(p)) page = <p className="muted">That’s part of BlueHQ. Your agency uses CivilHound.</p>;

  const allowed = (n: NavItem) => screenInProduct(n.to) && ([n.perm].flat().length === 0 || [n.perm].flat().some((x) => can(x)));
  const mods = MODULES.map((mo) => ({ ...mo, label: mo.key === 'records' && !isBlueHQ() ? 'People' : mo.label, groups: mo.groups.map((g) => ({ ...g, items: g.items.filter(allowed) })).filter((g) => g.items.length) })).filter((mo) => mo.groups.length);
  const curKey = moduleFor(p);
  const jumpItems: JumpItem[] = [
    ...(can('reports.write') || can('reports.approve') ? [
      { to: '/reports?new=incident', label: 'New incident report', icon: 'plus', group: 'Start', words: 'create write report' },
      { to: '/reports?new=arrest', label: 'New arrest report', icon: 'plus', group: 'Start', words: 'booking' },
    ] : []),
    ...(can('papers.intake') ? [{ to: '/papers/new', label: 'New paper intake', icon: 'plus', group: 'Start', words: 'paper summons' }] : []),
    ...mods.flatMap((mo) => mo.groups.flatMap((g) => g.items.map((n) => ({ to: n.to, label: n.label, icon: n.icon, group: mo.label })))),
  ].filter((x, i, all) => all.findIndex((y) => y.to === x.to) === i);
  const cur = mods.find((mo) => mo.key === curKey) ?? mods[0];
  const isActive = (to: string) => (to.includes('?') ? path === to : p === to && !(to === '/evidence' && path.includes('tab=court'))) || (to !== '/' && p.startsWith(to + '/') && !(to === '/papers' && p === '/papers/new') && !(to === '/reports' && (p.startsWith('/reports/analytics') || p === '/reports/similar')) && !(to === '/equipment' && p === '/equipment/report') && !(to === '/dues' && p === '/dues/me') && !(to === '/schools' && /^\/schools\/(desk|safety|details)/.test(p)) && !(to === '/hr' && p.startsWith('/hr/applicants')) && !(to === '/pio' && p.startsWith('/pio/foia')) && !(to === '/warrants' && /^\/warrants\/(request|mine|queue|entry)$/.test(p)));

  return (
    <div className="office">
      <header className="topbar">
        <Link to={isBlueHQ() ? '/home' : '/'} className="brand">
          {isBlueHQ() ? (
            <>
              <BlueMark size={30} className="brand-icon" /> <BlueWordmark />
            </>
          ) : (
            <>
              <img src="/civilhound-icon-clear.webp" alt="" width={30} height={30} className="brand-icon" /> CivilHound
            </>
          )}
        </Link>
        <span className="agency">{me!.agency.name}</span>
        <nav className="modtabs" aria-label="Modules">
          {mods.map((mo) => (
            <Link key={mo.key} to={mo.groups[0]!.items[0]!.to} className={mo.key === cur?.key ? 'on' : ''}>
              {mo.label}
            </Link>
          ))}
        </nav>
        {(can('papers.read') || can('reports.read') || can('dispatch.view')) && <GlobalSearch />}
        {isBlueHQ() && <MailButton />}
        <UserMenu />
      </header>
      <QuickJump items={jumpItems} />
      <div className="office-body">
        <nav className="sidenav" aria-label="Main">
          {cur?.groups.map((g) => (
            <div key={g.title} style={{ display: 'contents' }}>
              <div className="sn-h">{g.title}</div>
              {g.items.map((n) => (
                <Link key={n.to} to={n.to} className={isActive(n.to) ? 'active' : ''}>
                  <Icon name={n.icon} />
                  <span>{n.label}</span>
                </Link>
              ))}
            </div>
          ))}
          {(can('attempts.record') || can('papers.read')) && (
            <div className="sn-foot">
              <Link to="/field">
                <Icon name="phone" />
                <span>Deputy app</span>
              </Link>
            </div>
          )}
        </nav>
        <main className="content">
          {scanMsg && (
            <div className="alert warn scan-toast" role="alert">
              {scanMsg}{' '}
              <button className="btn link sm" onClick={() => setScanMsg(null)}>
                Dismiss
              </button>
            </div>
          )}
          {page}
        </main>
      </div>
    </div>
  );
}
